/**
 * Browser cookie helpers for the Vite SPA (replaces Next `next/headers` cookies).
 */

function parseDocumentCookie(): Record<string, string> {
  if (typeof document === "undefined") return {};
  return Object.fromEntries(
    document.cookie.split("; ").reduce<Array<[string, string]>>((acc, part) => {
      if (!part) return acc;
      const eq = part.indexOf("=");
      const k = decodeURIComponent(eq === -1 ? part : part.slice(0, eq)).trim();
      const v = decodeURIComponent(eq === -1 ? "" : part.slice(eq + 1));
      acc.push([k, v]);
      return acc;
    }, []),
  );
}

const LS_SESSION_KEY = "x-api-key";

function persistSessionToLocalStorage(name: string, value: string): void {
  if (name !== "x-api-key" || typeof localStorage === "undefined") return;
  try {
    if (value && value.trim()) {
      localStorage.setItem(LS_SESSION_KEY, value);
    } else {
      localStorage.removeItem(LS_SESSION_KEY);
    }
  } catch {
    /* quota / private mode */
  }
}

export async function setCookieByName(
  name: string,
  value: string,
  options?: { maxAge?: number; path?: string; sameSite?: string; secure?: boolean },
): Promise<string | undefined> {
  if (typeof document === "undefined") return undefined;
  const path = options?.path ?? "/";
  const maxAge = options?.maxAge ?? 60 * 60 * 24 * 365;
  const sameSite = options?.sameSite ?? "Lax";
  const secure = options?.secure ?? false;
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=${path}; max-age=${maxAge}; SameSite=${sameSite}${secure ? "; Secure" : ""}`;
  persistSessionToLocalStorage(name, value);
  return (await getCookieByName(name)) ?? undefined;
}

export async function deleteCookieByName(name: string): Promise<void> {
  if (typeof document === "undefined") return;
  document.cookie = `${encodeURIComponent(name)}=; path=/; max-age=0`;
  if (name === "x-api-key") {
    persistSessionToLocalStorage(name, "");
  }
}

/** Synchronous read (e.g. HTTP client hooks). Same source as `getCookieByName`. */
export function getCookieSync(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  return parseDocumentCookie()[name];
}

/** Non-empty session token: cookie first, then localStorage (SPA fallback). */
export function getSessionApiKeySync(): string | undefined {
  const t = getCookieSync("x-api-key");
  if (t && t.trim()) return t.trim();
  if (typeof localStorage !== "undefined") {
    try {
      const ls = localStorage.getItem(LS_SESSION_KEY);
      if (ls && ls.trim()) return ls.trim();
    } catch {
      /* ignore */
    }
  }
  return undefined;
}

export async function getCookieByName(name: string): Promise<string | undefined> {
  if (typeof document === "undefined") return undefined;
  return parseDocumentCookie()[name];
}

export async function getCookieByNames(names: string[]): Promise<(string | undefined)[]> {
  const map = parseDocumentCookie();
  return names.map((n) => map[n]);
}
